import { universalAnalysisService, WorkflowExecutionRequest } from './universalAnalysisService';
import { fetchWithActivity } from '../utils/fetchWithActivity';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Workflow interfaces
export interface WorkflowData {
  clientId: string;
  projectId?: string;
  workflowType: 'new_business' | 'renewal' | 'document_comparison' | 'loss_run_analysis' | 'loss_run_analyzer' | 'loss_run_analyzer_v4' | 'loss_run_analyzer_v4_3' | 'year_over_year' | 'yoy_renewal' | 'personal_lines_yoy_renewal' | 'commercial_lines_exec_renewal' | 'research_tool' | 'universal_analysis' | 'certificate_of_effort' | 'coverage_analysis' | 'insurance_requirements' | 'financial_analysis' | 'client_renewal_presentation' | 'rate_analysis' | 'portfolio_analyzer' | 'homeowners_comparison' | 'personal_umbrella' | 'cyber_auto' | 'cyber_benchmark' | 'cyber_renewal_delta' | 'cyber_war_systemic' | 'cyber_ransomware_trap' | 'cyber_quote_comparison' | 'do_auto' | 'do_benchmark' | 'do_renewal_delta' | 'do_distress_scanner' | 'do_emerging_exposure' | 'home_auto' | 'home_owner_benchmark' | 'home_tenant_condo_benchmark' | 'home_quote_comparison' | 'home_renewal_delta' | 'home_dp_rental_benchmark' | 'home_dp_rental_renewal_delta' | 'home_hnw_benchmark' | 'plines_auto' | 'pa_benchmark' | 'pa_quote_comparison' | 'pa_renewal_delta' | 'umb_benchmark' | 'umb_quote_comparison' | 'umb_renewal_delta' | 'rt_auto' | 'rt_requirements_reviewer' | 'rt_conformance_alignment' | 'rt_compliance_verifier' | 'premdev_auto' | 'premdev_workers_comp' | 'premdev_prof_liability' | 'premdev_general_liability' | 'premdev_property' | 'premdev_package_bop' | 'lossrun_auto' | 'lossrun_fact_engine' | 'lossrun_mitigation_wc' | 're_auto' | 're_policy_extraction' | 're_risk_transfer' | 're_br_financials' | 're_zone_extractor' | 're_portfolio_brief' | 'cl_auto' | 'cl_gl_benchmark' | 'cl_property_benchmark' | 'cl_commercial_auto_benchmark' | 'cl_inland_marine_benchmark' | 'cl_excess_benchmark' | 'cl_package_benchmark' | 'cl_bop_benchmark' | 'cl_nytpao_flow' | 'cl_package_quote_comparison' | 'cl_bop_quote_comparison' | 'cl_xarch_quote_comparison' | 'cl_package_renewal_delta' | 'cl_bop_renewal_delta' | 'ca_auto' | 'ca_coverage_determination' | 'ca_portfolio_determination' | 'ca_carrier_position_audit' | 'ntr_auto' | 'ntr_first_party' | 'ntr_property_liability' | 'ntr_mgmt_professional';
  workflowStage: string;
  data: any;
  timestamp: Date;
}

export interface SavedWorkflow {
  id: string;
  clientId: string;
  projectId?: string;
  workflowType: string;
  workflowStage: string;
  workflowData: string;
  createdAt: Date;
  updatedAt: Date;
}

// Tool execution interfaces
export interface ResearchToolResult {
  id: string;
  content: string;
  summary: string;
  documents: any[];
}

export interface LossRunResult {
  id: string;
  content: string;
  analysis: string;
  documents: any[];
}

export interface ComparisonResult {
  id: string;
  content: string;
  comparisonType: string;
  documents: any[];
}

class WorkflowService {
  // Workflow state management
  async saveWorkflow(workflowData: WorkflowData): Promise<string> {
    try {
      const response = await fetchWithActivity(`${API_BASE_URL}/api/workflows/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify({
          client_id: workflowData.clientId,
          project_id: workflowData.projectId,
          workflow_type: workflowData.workflowType,
          workflow_stage: workflowData.workflowStage,
          workflow_data: JSON.stringify(workflowData.data),
          timestamp: new Date()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save workflow');
      }

      const result = await response.json();
      return result.id;
    } catch (error) {
      console.error('Error saving workflow:', error);
      throw error;
    }
  }

  async resumeWorkflow(workflowId: string): Promise<SavedWorkflow> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/${workflowId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load workflow');
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading workflow:', error);
      throw error;
    }
  }

  async getClientWorkflows(clientId: string): Promise<SavedWorkflow[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/client/${clientId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get client workflows');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting client workflows:', error);
      throw error;
    }
  }

  // Tool execution methods
  async executeResearchTool(clientId: string, projectId?: string, context?: any, onProgress?: (progress: number) => void): Promise<ResearchToolResult> {
    try {
      // Start the research workflow (now runs in background)
      const response = await fetch(`${API_BASE_URL}/api/workflows/research/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify({
          url: context?.url || '',
          linkedinUrl: context?.linkedinUrl || '',
          companyName: context?.companyName || '',
          additionalText: context?.additionalText || '',
          documents: context?.documents || [],
          clientId,
          projectId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start research tool');
      }

      const result = await response.json();

      // If the response contains a jobId, poll for completion
      if (result.status === 'processing' && result.jobId) {
        console.log(`🔄 Research job ${result.jobId} started, polling for completion...`);
        return await this.pollResearchJobStatus(result.jobId, onProgress);
      }

      // For backward compatibility, return direct result if provided
      return result;
    } catch (error) {
      console.error('Error executing research tool:', error);
      throw error;
    }
  }

  /**
   * Poll for research job completion
   */
  private async pollResearchJobStatus(jobId: string, onProgress?: (progress: number) => void): Promise<ResearchToolResult> {
    const maxAttempts = 450; // 15 minutes at 2s intervals
    const pollInterval = 2000;
    let attempts = 0;

    while (attempts < maxAttempts) {
      attempts++;

      // Wait before polling (except on first attempt)
      if (attempts > 1) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }

      try {
        const statusResponse = await fetch(`${API_BASE_URL}/api/workflows/research/status/${jobId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!statusResponse.ok) {
          if (statusResponse.status === 404) {
            throw new Error('Research job not found');
          }
          console.error(`Status check failed (attempt ${attempts}):`, statusResponse.status);
          continue;
        }

        const statusData = await statusResponse.json();

        // Report progress
        if (onProgress && statusData.job?.progress !== undefined) {
          onProgress(statusData.job.progress);
        }

        // Check if job completed
        if (statusData.job?.status === 'completed') {
          console.log('✅ Research job completed successfully');
          return statusData.job.result;
        }

        // Check if job failed
        if (statusData.job?.status === 'failed') {
          console.error('❌ Research job failed:', statusData.job.error);
          throw new Error(statusData.job.error || 'Research analysis failed');
        }

        console.log(`🔄 Research polling attempt ${attempts}/${maxAttempts} - Progress: ${statusData.job?.progress || 0}%`);

      } catch (error: any) {
        if (error.message?.includes('not found')) {
          throw error;
        }
        console.error(`Error polling research status (attempt ${attempts}):`, error);
        if (attempts >= maxAttempts) {
          throw new Error('Research job polling timeout');
        }
      }
    }

    throw new Error('Research job polling timeout');
  }

  async executeLossRunTool(clientId: string, projectId?: string, context?: any): Promise<LossRunResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/loss-run/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify({
          clientId,
          projectId,
          context,
          documents: context?.documents || []
        })
      });

      if (!response.ok) {
        throw new Error('Failed to execute loss run tool');
      }

      return await response.json();
    } catch (error) {
      console.error('Error executing loss run tool:', error);
      throw error;
    }
  }

  async compareApplications(data: {
    new: File[] | any[]; // Support both File objects and uploaded document objects
    previous: any[];
    context?: string;
    projectId?: string;
  }): Promise<ComparisonResult> {
    try {
      // Convert File objects to document IDs if necessary
      const formatDocuments = (docs: any[]) => {
        return docs.map(doc => {
          if (doc instanceof File) {
            // If it's a File object, we need to handle this differently
            // For now, return the file info (this should be rare now)
            return { name: doc.name, size: doc.size, type: doc.type };
          } else if (doc.id) {
            // If it's an uploaded document object, return its ID
            return { id: doc.id, name: doc.name };
          }
          return doc;
        });
      };

      // Use workflow endpoint for application comparison
      const response = await fetch(`${API_BASE_URL}/api/workflows/comparison/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify({
          newDocuments: formatDocuments(data.new),
          previousDocuments: formatDocuments(data.previous),
          context: data.context,
          projectId: data.projectId,
          options: {
            useQuoteCitations: true  // Enable quote citations by default
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to compare applications');
      }

      return await response.json();
    } catch (error) {
      console.error('Error comparing applications:', error);
      throw error;
    }
  }

  async runDocumentComparison(params: {
    documents: any[];
    additionalDocuments?: any[];
    type: string;
    additionalContext?: string;
    customerId?: string;
    projectId?: string;
    pastReports?: any[];
    reportTitle?: string;
  }): Promise<ComparisonResult> {
    // Retry wrapper for timeouts, failures, and backend job errors
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing document comparison (attempt ${attempt}/${maxRetries})...`);
        return await this.executeComparisonWithTimeout(params);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Workflow attempt ${attempt} failed:`, error);

        // Check if it's a retryable error
        const isTimeout = error instanceof Error && (
          error.message.includes('aborted') ||
          error.message.includes('timeout') ||
          error.name === 'AbortError'
        );

        const isJsonError = error instanceof Error && error.message.includes('JSON');

        // Also retry on backend job failures (e.g., Vertex AI errors)
        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');

        const isRetryable = isTimeout || isJsonError || isJobFailure;

        if (isRetryable && attempt < maxRetries) {
          const retryDelay = attempt * 5; // Progressive delay: 5s, 10s
          const reason = isJobFailure ? 'backend job failure' : (isTimeout ? 'timeout' : 'JSON parsing failure');
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${reason}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} workflow attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Workflow execution failed after all retries');
  }

  private async executeComparisonWithTimeout(params: {
    documents: any[];
    additionalDocuments?: any[];
    type: string;
    additionalContext?: string;
    customerId?: string;
    projectId?: string;
    pastReports?: any[];
    reportTitle?: string;
  }): Promise<ComparisonResult> {
    try {
      // Validate params
      if (!params || !params.documents) {
        throw new Error('Documents parameter is required for comparison');
      }

      // Extract document IDs from main documents array
      const documentIds = params.documents
        .filter(doc => doc && doc.id && !doc.isNew)
        .map(doc => doc.id);

      // Extract additional document IDs if provided
      const additionalDocIds = (params.additionalDocuments || [])
        .filter(doc => doc && doc.id && !doc.isNew)
        .map(doc => doc.id);

      // Check if we have enough items total (documents + past reports)
      const totalItems = documentIds.length + (params.pastReports?.length || 0);
      if (totalItems < 2) {
        throw new Error('At least 2 items (documents or reports) required for comparison');
      }

      // No timeout - let the server's 20-minute timeout handle it
      const response = await fetch(`${API_BASE_URL}/api/workflows/document-comparison/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify({
          documents: params.documents,
          additionalContext: params.additionalContext,
          customerId: params.customerId,
          projectId: params.projectId,
          reportTitle: params.reportTitle
        })
      });
      
      if (!response.ok) {
        throw new Error('Comparison request failed');
      }
      
      const initialResult = await response.json();

      console.log('✅ Document comparison started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      // Check if this is the new async response format
      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');
        const token = localStorage.getItem('riskninja-token') || '';
        const finalResult = await this.pollDocumentComparisonJobStatus(initialResult.jobId, token);
        return finalResult;
      }

      // Old format (synchronous) - return directly
      return initialResult;
    } catch (error) {
      console.error('Error running document comparison:', error);
      throw error;
    }
  }

  async executeLossRunAnalysis(data: {
    documents: any[];
    clientId: string;
    projectId?: string;
    pastReports?: any[];
    reportTitle?: string;
  }): Promise<any> {
    // Retry wrapper for backend job failures
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing loss run analysis (attempt ${attempt}/${maxRetries})...`);
        return await this.executeLossRunAnalysisInternal(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Loss run attempt ${attempt} failed:`, error);

        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');
        const isTimeout = error instanceof Error && (
          error.message.includes('timeout') || error.message.includes('aborted')
        );

        if ((isJobFailure || isTimeout) && attempt < maxRetries) {
          const retryDelay = attempt * 5;
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${isJobFailure ? 'backend job failure' : 'timeout'}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} loss run attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Loss run analysis failed after all retries');
  }

  private async executeLossRunAnalysisInternal(data: {
    documents: any[];
    clientId: string;
    projectId?: string;
    pastReports?: any[];
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting loss run analysis with data:', {
        documentsCount: data.documents?.length,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        documentTypes: data.documents?.map(d => ({ id: d.id, name: d.name, type: d.type })),
        apiBaseUrl: API_BASE_URL,
        hasToken: !!localStorage.getItem('riskninja-token'),
        validUUIDs: data.documents?.map(d => ({
          id: d.id,
          isValidUUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(d.id),
          isTimestamp: typeof d.id === 'number' || (typeof d.id === 'string' && /^\d+\.\d+$/.test(d.id))
        }))
      });

      // Get current token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/loss-run/analyze`;
      console.log('📡 Making request to:', requestUrl);

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Loss run analysis failed with status:', response.status, errorText);
        
        // If it's a 403 error, try to refresh the token or provide more info
        if (response.status === 403) {
          console.error('❌ 403 Forbidden - Check if token is valid and project access is correct');
        }
        
        throw new Error(`Failed to analyze loss runs (${response.status}): ${errorText}`);
      }

      const initialResult = await response.json();
      console.log('✅ Loss run analysis started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      // Check if this is the new async response format
      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');

        // Poll for job completion
        const finalResult = await this.pollLossRunJobStatus(initialResult.jobId, token);
        return finalResult;
      }

      // Old format (synchronous) - return directly
      return initialResult;
    } catch (error) {
      console.error('❌ Error analyzing loss runs:', error);
      
      // Log additional debugging info
      console.error('❌ Error details:', {
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        dataProvided: {
          documentsCount: data.documents?.length,
          hasClientId: !!data.clientId,
          hasProjectId: !!data.projectId
        }
      });
      
      throw error;
    }
  }

  private async pollLossRunJobStatus(jobId: string, token: string, maxAttempts: number = 450): Promise<any> {
    const pollInterval = 2000; // 2 seconds
    let attempts = 0;

    // Emit progress updates through console (can be enhanced with callbacks)
    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Progress: ${progress}% - ${this.getProgressMessage(progress)}`);

      // Store progress in localStorage for UI to read
      localStorage.setItem('lossRunProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      // Dispatch custom event for real-time UI updates
      window.dispatchEvent(new CustomEvent('lossRunProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/loss-run/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        // Emit progress update
        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Loss run analysis completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          // Clean up progress tracking
          localStorage.removeItem('lossRunProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('lossRunProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }

        // Still processing, continue polling
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        // Re-throw job failures immediately - don't swallow them
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('lossRunProgress');
          throw new Error('Job status polling timeout');
        }
        // Continue polling on transient errors
      }
    }

    localStorage.removeItem('lossRunProgress');
    throw new Error('Job completion timeout (exceeded 15 minutes)');
  }

  private getProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Loading past reports...';
    if (progress < 40) return 'Preparing analysis...';
    if (progress < 50) return 'Generating prompts...';
    if (progress < 70) return 'AI analyzing loss runs... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  // ============================================================================
  // Loss Run Analyzer (Forensic Multi-Document Analysis)
  // ============================================================================

  async executeLossRunAnalyzer(data: {
    documents: any[];
    clientId: string;
    projectId?: string;
    context?: string;
    pastReports?: any[];
    reportTitle?: string;
  }): Promise<any> {
    // Retry wrapper for backend job failures
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing loss run analyzer (attempt ${attempt}/${maxRetries})...`);
        return await this.executeLossRunAnalyzerInternal(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Loss run analyzer attempt ${attempt} failed:`, error);

        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');
        const isTimeout = error instanceof Error && (
          error.message.includes('timeout') || error.message.includes('aborted')
        );

        if ((isJobFailure || isTimeout) && attempt < maxRetries) {
          const retryDelay = attempt * 5;
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${isJobFailure ? 'backend job failure' : 'timeout'}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} loss run analyzer attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Loss run analyzer failed after all retries');
  }

  private async executeLossRunAnalyzerInternal(data: {
    documents: any[];
    clientId: string;
    projectId?: string;
    context?: string;
    pastReports?: any[];
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting loss run analyzer with data:', {
        documentsCount: data.documents?.length,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        pastReportsCount: data.pastReports?.length || 0,
        apiBaseUrl: API_BASE_URL
      });

      // Get current token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/loss-run-analyzer/analyze`;
      console.log('📡 Making request to:', requestUrl);

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Loss run analyzer failed with status:', response.status, errorText);

        if (response.status === 403) {
          console.error('❌ 403 Forbidden - Check if token is valid and project access is correct');
        }

        throw new Error(`Failed to analyze loss runs (${response.status}): ${errorText}`);
      }

      const initialResult = await response.json();
      console.log('✅ Loss run analyzer started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      // Check if this is the async response format
      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');

        // Poll for job completion
        const finalResult = await this.pollLossRunAnalyzerJobStatus(initialResult.jobId, token);
        return finalResult;
      }

      // Synchronous response - return directly
      return initialResult;
    } catch (error) {
      console.error('❌ Error in loss run analyzer:', error);

      console.error('❌ Error details:', {
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        dataProvided: {
          documentsCount: data.documents?.length,
          hasClientId: !!data.clientId,
          hasProjectId: !!data.projectId
        }
      });

      throw error;
    }
  }

  private async pollLossRunAnalyzerJobStatus(jobId: string, token: string, maxAttempts: number = 450): Promise<any> {
    const pollInterval = 2000; // 2 seconds
    let attempts = 0;

    // Emit progress updates through console and events
    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Progress: ${progress}% - ${this.getLossRunAnalyzerProgressMessage(progress)}`);

      // Store progress in localStorage for UI to read
      localStorage.setItem('lossRunAnalyzerProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      // Dispatch custom event for real-time UI updates
      window.dispatchEvent(new CustomEvent('lossRunAnalyzerProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/loss-run-analyzer/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        // Emit progress update
        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Loss run analyzer completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          // Clean up progress tracking
          localStorage.removeItem('lossRunAnalyzerProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('lossRunAnalyzerProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }

        // Still processing, continue polling
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        // Re-throw job failures immediately - don't swallow them
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('lossRunAnalyzerProgress');
          throw new Error('Job status polling timeout');
        }
        // Continue polling on transient errors
      }
    }

    localStorage.removeItem('lossRunAnalyzerProgress');
    throw new Error('Job completion timeout (exceeded 15 minutes)');
  }

  private getLossRunAnalyzerProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing forensic analysis...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Detecting loss run formats...';
    if (progress < 40) return 'Building claim census...';
    if (progress < 50) return 'Preparing extraction prompts...';
    if (progress < 70) return 'AI analyzing loss runs... (this may take 3-7 minutes)';
    if (progress < 80) return 'Cross-document reconciliation...';
    if (progress < 90) return 'Generating pattern analysis...';
    if (progress < 100) return 'Saving report...';
    return 'Complete!';
  }

  async executeCoverageAnalysis(data: {
    documents: any[];
    analysisMode: string;
    additionalInfo?: string;
    scenarioText?: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    // Retry wrapper for backend job failures
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing coverage analysis (attempt ${attempt}/${maxRetries})...`);
        return await this.executeCoverageAnalysisInternal(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Coverage analysis attempt ${attempt} failed:`, error);

        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');
        const isTimeout = error instanceof Error && (
          error.message.includes('timeout') || error.message.includes('aborted')
        );

        if ((isJobFailure || isTimeout) && attempt < maxRetries) {
          const retryDelay = attempt * 5;
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${isJobFailure ? 'backend job failure' : 'timeout'}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} coverage analysis attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Coverage analysis failed after all retries');
  }

  private async executeCoverageAnalysisInternal(data: {
    documents: any[];
    analysisMode: string;
    additionalInfo?: string;
    scenarioText?: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting coverage analysis with data:', {
        documentsCount: data.documents?.length,
        analysisMode: data.analysisMode,
        scenarioText: data.scenarioText,
        additionalInfo: data.additionalInfo,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        documentTypes: data.documents?.map(d => ({ id: d.id, name: d.name, type: d.type })),
        apiBaseUrl: API_BASE_URL,
        hasToken: !!localStorage.getItem('riskninja-token')
      });

      // Get current token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/coverage_analysis`;
      console.log('📡 Making request to:', requestUrl);

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Coverage analysis failed with status:', response.status, errorText);
        
        // If it's a 403 error, try to refresh the token or provide more info
        if (response.status === 403) {
          console.error('❌ 403 Forbidden - Check if token is valid and project access is correct');
        }
        
        throw new Error(`Failed to analyze coverage (${response.status}): ${errorText}`);
      }

      const initialResult = await response.json();
      console.log('✅ Coverage analysis started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      // Check if this is the new async response format
      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');
        const token = localStorage.getItem('riskninja-token') || '';
        const finalResult = await this.pollCoverageAnalysisJobStatus(initialResult.jobId, token);
        return finalResult;
      }

      // Old format (synchronous) - return directly
      return initialResult;
    } catch (error) {
      console.error('❌ Error analyzing coverage:', error);
      
      // Log additional debugging info
      console.error('❌ Error details:', {
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        dataProvided: {
          documentsCount: data.documents?.length,
          hasAnalysisMode: !!data.analysisMode,
          hasClientId: !!data.clientId,
          hasProjectId: !!data.projectId
        }
      });
      
      throw error;
    }
  }

  private async pollCoverageAnalysisJobStatus(jobId: string, token: string, maxAttempts: number = 600): Promise<any> {
    const pollInterval = 3000; // 3 seconds - poll for up to 30 minutes
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Coverage Analysis Progress: ${progress}% - ${this.getCoverageAnalysisProgressMessage(progress)}`);

      localStorage.setItem('coverageAnalysisProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('coverageAnalysisProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/coverage-analysis/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Coverage Analysis Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Coverage analysis completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          localStorage.removeItem('coverageAnalysisProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('coverageAnalysisProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        // Re-throw job failures immediately - don't swallow them
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('coverageAnalysisProgress');
          throw new Error('Job status polling timeout');
        }
      }
    }

    localStorage.removeItem('coverageAnalysisProgress');
    throw new Error('Job completion timeout (exceeded 15 minutes)');
  }

  private getCoverageAnalysisProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Preparing document content...';
    if (progress < 40) return 'Generating coverage prompts...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI analyzing coverage... (this may take a few minutes)';
    if (progress < 80) return 'Processing results...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  async executeYearOverYearAnalysis(data: {
    documents: any[];
    mode: string;
    clientId: string;
    projectId?: string;
    context?: any;
    reportTitle?: string;
  }): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/comparison/year-over-year`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify({
          ...data,
          analysisType: 'year-over-year',
          options: {
            useQuoteCitations: true  // Enable quote citations by default
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to execute year-over-year analysis');
      }

      return await response.json();
    } catch (error) {
      console.error('Error executing year-over-year analysis:', error);
      throw error;
    }
  }

  async executeRenewalAnalysis(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    pastReports?: any[];
    reportTitle?: string;
  }): Promise<any> {
    // Retry wrapper for timeouts, failures, and backend job errors
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing renewal analysis (attempt ${attempt}/${maxRetries})...`);
        return await this.executeRenewalWithTimeout(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Renewal attempt ${attempt} failed:`, error);

        // Check if it's a retryable error
        const isTimeout = error instanceof Error && (
          error.message.includes('aborted') ||
          error.message.includes('timeout') ||
          error.name === 'AbortError'
        );

        const isJsonError = error instanceof Error && error.message.includes('JSON');

        // Also retry on backend job failures (e.g., Vertex AI errors)
        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');

        const isRetryable = isTimeout || isJsonError || isJobFailure;

        if (isRetryable && attempt < maxRetries) {
          const retryDelay = attempt * 5; // Progressive delay: 5s, 10s
          const reason = isJobFailure ? 'backend job failure' : (isTimeout ? 'timeout' : 'JSON parsing failure');
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${reason}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} renewal attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Renewal analysis failed after all retries');
  }

  private async executeRenewalWithTimeout(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    pastReports?: any[];
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting renewal analysis with data:', {
        documentsCount: data.documents?.length,
        pastReportsCount: data.pastReports?.length,
        additionalContext: data.additionalContext,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        documentTypes: data.documents?.map(d => ({ id: d.id, name: d.name, type: d.type })),
        pastReportIds: data.pastReports?.map(r => r.id),
        apiBaseUrl: API_BASE_URL,
        hasToken: !!localStorage.getItem('riskninja-token')
      });

      // Get current token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/renewal/execute`;
      console.log('📡 Making request to:', requestUrl);

      // No timeout - let the server's 20-minute timeout handle it
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Renewal analysis request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`Failed to execute renewal analysis: ${response.status} ${response.statusText}`);
      }

      const initialResult = await response.json();
      console.log('✅ Renewal analysis started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      // Check if this is the new async response format
      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');
        const finalResult = await this.pollRenewalJobStatus(initialResult.jobId, token);
        // Include the server workflowId in the result for background tracking
        if (initialResult.workflowId) {
          finalResult.serverWorkflowId = initialResult.workflowId;
        }
        return finalResult;
      }

      // Old format (synchronous) - return directly
      return initialResult;

    } catch (error) {
      console.error('❌ Error executing renewal analysis:', error);
      console.error('Error details:', {
        documentsArray: data.documents?.map(d => ({ id: d.id, name: d.name })),
        dataProvided: {
          documentsCount: data.documents?.length,
          hasClientId: !!data.clientId,
          hasProjectId: !!data.projectId,
          hasAdditionalContext: !!data.additionalContext
        }
      });

      throw error;
    }
  }

  private async pollRenewalJobStatus(jobId: string, token: string, maxAttempts: number = 600): Promise<any> {
    const pollInterval = 3000; // 3 seconds - poll for up to 30 minutes
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Renewal Progress: ${progress}% - ${this.getRenewalProgressMessage(progress)}`);

      localStorage.setItem('renewalProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('renewalProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/renewal/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Renewal Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Renewal analysis completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          localStorage.removeItem('renewalProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('renewalProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        // Re-throw job failures immediately - don't swallow them
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('renewalProgress');
          throw new Error('Job status polling timeout');
        }
      }
    }

    localStorage.removeItem('renewalProgress');
    throw new Error('Job completion timeout (exceeded 15 minutes)');
  }

  private getRenewalProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Loading past reports...';
    if (progress < 40) return 'Preparing analysis...';
    if (progress < 50) return 'Generating prompts...';
    if (progress < 70) return 'AI analyzing renewal options... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  private async pollDocumentComparisonJobStatus(jobId: string, token: string, maxAttempts: number = 600): Promise<any> {
    const pollInterval = 3000; // 3 seconds - poll for up to 30 minutes (600 * 3 = 1800 seconds)
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Document Comparison Progress: ${progress}% - ${this.getDocumentComparisonProgressMessage(progress)}`);

      localStorage.setItem('documentComparisonProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('documentComparisonProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/document-comparison/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Document Comparison Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Document comparison completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          localStorage.removeItem('documentComparisonProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('documentComparisonProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        // Re-throw job failures immediately - don't swallow them
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('documentComparisonProgress');
          throw new Error('Job status polling timeout');
        }
      }
    }

    localStorage.removeItem('documentComparisonProgress');
    throw new Error('Job completion timeout (exceeded 30 minutes)');
  }

  private getDocumentComparisonProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Preparing documents...';
    if (progress < 40) return 'Generating comparison prompts...';
    if (progress < 50) return 'Starting AI comparison...';
    if (progress < 70) return 'AI comparing documents... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  async executeYearOverYearAnalysisNew(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    pastReports?: any[];
    reportTitle?: string;
  }): Promise<any> {
    // Retry wrapper for timeouts, failures, and backend job errors
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing year-over-year analysis (attempt ${attempt}/${maxRetries})...`);
        return await this.executeYearOverYearWithTimeout(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Year-over-year attempt ${attempt} failed:`, error);

        // Check if it's a retryable error
        const isTimeout = error instanceof Error && (
          error.message.includes('aborted') ||
          error.message.includes('timeout') ||
          error.name === 'AbortError'
        );

        const isJsonError = error instanceof Error && error.message.includes('JSON');

        // Also retry on backend job failures (e.g., Vertex AI errors)
        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');

        const isRetryable = isTimeout || isJsonError || isJobFailure;

        if (isRetryable && attempt < maxRetries) {
          const retryDelay = attempt * 5; // Progressive delay: 5s, 10s
          const reason = isJobFailure ? 'backend job failure' : (isTimeout ? 'timeout' : 'JSON parsing failure');
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${reason}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} year-over-year attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Year-over-year analysis failed after all retries');
  }

  private async executeYearOverYearWithTimeout(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    pastReports?: any[];
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting year-over-year analysis with data:', {
        documentsCount: data.documents?.length,
        additionalContext: data.additionalContext,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        documentTypes: data.documents?.map(d => ({ id: d.id, name: d.name, type: d.type })),
        apiBaseUrl: API_BASE_URL,
        hasToken: !!localStorage.getItem('riskninja-token')
      });

      // Get current token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/year-over-year/execute`;
      console.log('📡 Making request to:', requestUrl);

      // No timeout - let the server's 20-minute timeout handle it
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Year-over-year analysis request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`Failed to execute year-over-year analysis: ${response.status} ${response.statusText}`);
      }

      const initialResult = await response.json();
      console.log('✅ Year-over-year analysis started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      // Check if this is the new async response format
      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');
        const finalResult = await this.pollYearOverYearJobStatus(initialResult.jobId, token);
        return finalResult;
      }

      // Old format (synchronous) - return directly
      return initialResult;

    } catch (error) {
      console.error('❌ Error executing year-over-year analysis:', error);
      console.error('Error details:', {
        documentsArray: data.documents?.map(d => ({ id: d.id, name: d.name })),
        dataProvided: {
          documentsCount: data.documents?.length,
          hasClientId: !!data.clientId,
          hasProjectId: !!data.projectId,
          hasAdditionalContext: !!data.additionalContext
        }
      });

      throw error;
    }
  }

  private async pollYearOverYearJobStatus(jobId: string, token: string, maxAttempts: number = 600): Promise<any> {
    const pollInterval = 3000; // 3 seconds - poll for up to 30 minutes
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Year Over Year Progress: ${progress}% - ${this.getYearOverYearProgressMessage(progress)}`);

      localStorage.setItem('yearOverYearProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('yearOverYearProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/year-over-year/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Year Over Year Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Year-over-year analysis completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          localStorage.removeItem('yearOverYearProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('yearOverYearProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        // Re-throw job failures immediately - don't swallow them
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('yearOverYearProgress');
          throw new Error('Job status polling timeout');
        }
      }
    }

    localStorage.removeItem('yearOverYearProgress');
    throw new Error('Job completion timeout (exceeded 15 minutes)');
  }

  private getYearOverYearProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Loading past reports...';
    if (progress < 40) return 'Preparing analysis...';
    if (progress < 50) return 'Generating prompts...';
    if (progress < 70) return 'AI analyzing year-over-year trends... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  /**
   * Execute YOY Renewal workflow via unified endpoint
   * Supports yoy_renewal, personal_lines_yoy_renewal, and commercial_lines_exec_renewal
   */
  async executeYoyRenewalAnalysis(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    pastReports?: any[];
    workflowType?: 'yoy_renewal' | 'personal_lines_yoy_renewal' | 'commercial_lines_exec_renewal';
    reportTitle?: string;
    reportOptions?: Record<string, boolean>;
  }): Promise<any> {
    // Retry wrapper for timeouts, failures, and backend job errors
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing YOY renewal analysis (attempt ${attempt}/${maxRetries})...`);
        return await this.executeYoyRenewalWithTimeout(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ YOY renewal attempt ${attempt} failed:`, error);

        // Check if it's a retryable error
        const isTimeout = error instanceof Error && (
          error.message.includes('aborted') ||
          error.message.includes('timeout') ||
          error.name === 'AbortError'
        );

        const isJsonError = error instanceof Error && error.message.includes('JSON');

        // Also retry on backend job failures (e.g., Vertex AI errors)
        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');

        const isRetryable = isTimeout || isJsonError || isJobFailure;

        if (isRetryable && attempt < maxRetries) {
          const retryDelay = attempt * 5; // Progressive delay: 5s, 10s
          const reason = isJobFailure ? 'backend job failure' : (isTimeout ? 'timeout' : 'JSON parsing failure');
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${reason}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} YOY renewal attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('YOY renewal analysis failed after all retries');
  }

  private async executeYoyRenewalWithTimeout(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    pastReports?: any[];
    workflowType?: 'yoy_renewal' | 'personal_lines_yoy_renewal' | 'commercial_lines_exec_renewal';
    reportTitle?: string;
    reportOptions?: Record<string, boolean>;
  }): Promise<any> {
    const effectiveWorkflowType = data.workflowType || 'yoy_renewal';
    try {
      console.log('🔄 Starting YOY renewal analysis with data:', {
        documentsCount: data.documents?.length,
        additionalContext: data.additionalContext,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        documentTypes: data.documents?.map(d => ({ id: d.id, name: d.name, type: d.type })),
        apiBaseUrl: API_BASE_URL,
        hasToken: !!localStorage.getItem('riskninja-token')
      });

      // Get current token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      // Use unified workflow endpoint for the specified workflow type
      const requestUrl = `${API_BASE_URL}/api/workflows/unified/execute/${effectiveWorkflowType}`;
      console.log('📡 Making request to:', requestUrl, 'for workflow type:', effectiveWorkflowType);

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          documents: data.documents.map(d => d.id || d),
          clientId: data.clientId,
          projectId: data.projectId,
          context: data.additionalContext,
          pastReports: data.pastReports,
          reportTitle: data.reportTitle,
          reportOptions: data.reportOptions,
        })
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ YOY renewal analysis request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`YOY renewal analysis failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📡 YOY renewal initial response:', result);

      if (result.success && result.status === 'processing' && result.jobId) {
        // Poll for job completion using unified status endpoint
        return await this.pollYoyRenewalJobStatus(result.jobId, token);
      }

      return result;
    } catch (error) {
      console.error('❌ Error executing YOY renewal analysis:', error);
      throw error;
    }
  }

  private async pollYoyRenewalJobStatus(jobId: string, token: string): Promise<any> {
    const maxAttempts = 180; // 15 minutes at 5 second intervals
    const pollInterval = 5000;
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ YOY Renewal Progress: ${progress}% - ${this.getYoyRenewalProgressMessage(progress)}`);

      localStorage.setItem('yoyRenewalProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('yoyRenewalProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/unified/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 YOY Renewal Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ YOY renewal analysis completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          localStorage.removeItem('yoyRenewalProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('yoyRenewalProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('yoyRenewalProgress');
          throw new Error('Job status polling timeout');
        }
      }
    }

    localStorage.removeItem('yoyRenewalProgress');
    throw new Error('Job completion timeout (exceeded 15 minutes)');
  }

  private getYoyRenewalProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Loading past reports...';
    if (progress < 40) return 'Preparing analysis...';
    if (progress < 50) return 'Generating prompts with domain expertise...';
    if (progress < 70) return 'AI analyzing renewal trends... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  async executeInsuranceSummary(data: {
    documentIds: string[];
    clientId: string;
    projectId: string;
    reportTitle?: string;
    additionalContext?: string;
    onProgress?: (progress: number, message: string) => void;
  }): Promise<any> {
    const token = localStorage.getItem('riskninja-token');
    if (!token) throw new Error('Authentication token is missing');

    // Submit to the BullMQ-backed queue (same path other workflows use).
    const response = await fetch(`${API_BASE_URL}/api/jobs/workflows`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        workflowType: 'insurance_summary',
        customerId: data.clientId,
        projectId: data.projectId,
        documents: data.documentIds,
        reportTitle: data.reportTitle,
        context: data.additionalContext,
        additionalContext: data.additionalContext,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Insurance Summary submit failed: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    if (!result.success || !result.jobId) {
      throw new Error('Insurance Summary submit returned no jobId');
    }

    const jobId = result.jobId;

    const fallbackMessage = (progress: number): string =>
      progress < 20 ? 'Loading documents…' :
      progress < 40 ? 'Preparing prompt…' :
      progress < 70 ? 'AI building summary…' :
      progress < 90 ? 'Processing citations…' :
      progress < 100 ? 'Saving…' : 'Done';

    const maxAttempts = 180; // 15 minutes at 5s
    const pollInterval = 5000;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      await new Promise((r) => setTimeout(r, pollInterval));

      const statusRes = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!statusRes.ok) continue;

      const statusJson = await statusRes.json();
      const job = statusJson.job;
      if (!job) continue;

      const progress = job.progress ?? 0;
      const status = job.status ?? 'processing';
      const message = job.progressMessage || fallbackMessage(progress);

      if (data.onProgress) data.onProgress(progress, message);

      if (status === 'completed') {
        return job.resultPayload || { savedReportId: job.savedReportId };
      }
      if (status === 'failed' || status === 'dead_letter' || status === 'cancelled') {
        throw new Error(job.lastError || `Summary job ${status}`);
      }
    }
    throw new Error('Summary job timed out after 15 minutes');
  }

  async executeInsuranceRequirementsAnalysis(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    // Retry wrapper for backend job failures
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing insurance requirements analysis (attempt ${attempt}/${maxRetries})...`);
        return await this.executeInsuranceRequirementsInternal(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Insurance requirements attempt ${attempt} failed:`, error);

        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');
        const isTimeout = error instanceof Error && (
          error.message.includes('timeout') || error.message.includes('aborted')
        );

        if ((isJobFailure || isTimeout) && attempt < maxRetries) {
          const retryDelay = attempt * 5;
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${isJobFailure ? 'backend job failure' : 'timeout'}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} insurance requirements attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Insurance requirements analysis failed after all retries');
  }

  private async executeInsuranceRequirementsInternal(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting insurance requirements analysis with data:', {
        documentsCount: data.documents?.length,
        additionalContext: data.additionalContext,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        documentTypes: data.documents?.map(d => ({ id: d.id, name: d.name, type: d.type })),
        apiBaseUrl: API_BASE_URL,
        hasToken: !!localStorage.getItem('riskninja-token')
      });

      // Get current token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/insurance-requirements/execute`;
      console.log('📡 Making request to:', requestUrl);

      // No timeout - let the server's 20-minute timeout handle it
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Insurance requirements analysis request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`Failed to execute insurance requirements analysis: ${response.status} ${response.statusText}`);
      }

      const initialResult = await response.json();
      console.log('✅ Insurance requirements analysis started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      // Check if this is the new async response format
      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');
        const finalResult = await this.pollInsuranceRequirementsJobStatus(initialResult.jobId, token);
        return finalResult;
      }

      // Old format (synchronous) - return directly
      return initialResult;

    } catch (error) {
      console.error('❌ Error executing insurance requirements analysis:', error);
      console.error('Error details:', {
        documentsArray: data.documents?.map(d => ({ id: d.id, name: d.name })),
        dataProvided: {
          documentsCount: data.documents?.length,
          hasClientId: !!data.clientId,
          hasProjectId: !!data.projectId,
          hasAdditionalContext: !!data.additionalContext
        }
      });

      throw error;
    }
  }

  private async pollInsuranceRequirementsJobStatus(jobId: string, token: string, maxAttempts: number = 600): Promise<any> {
    const pollInterval = 3000; // 3 seconds - poll for up to 30 minutes
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Insurance Requirements Progress: ${progress}% - ${this.getInsuranceRequirementsProgressMessage(progress)}`);

      localStorage.setItem('insuranceRequirementsProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('insuranceRequirementsProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/insurance-requirements/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Insurance Requirements Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Insurance requirements analysis completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          localStorage.removeItem('insuranceRequirementsProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('insuranceRequirementsProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        // Re-throw job failures immediately - don't swallow them
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('insuranceRequirementsProgress');
          throw new Error('Job status polling timeout');
        }
      }
    }

    localStorage.removeItem('insuranceRequirementsProgress');
    throw new Error('Job completion timeout (exceeded 15 minutes)');
  }

  private getInsuranceRequirementsProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Preparing documents...';
    if (progress < 40) return 'Generating prompts...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI analyzing insurance requirements... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  async executeFinancialAnalysis(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    // Retry wrapper for backend job failures
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing financial analysis (attempt ${attempt}/${maxRetries})...`);
        return await this.executeFinancialAnalysisInternal(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Financial analysis attempt ${attempt} failed:`, error);

        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');
        const isTimeout = error instanceof Error && (
          error.message.includes('timeout') || error.message.includes('aborted')
        );

        if ((isJobFailure || isTimeout) && attempt < maxRetries) {
          const retryDelay = attempt * 5;
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${isJobFailure ? 'backend job failure' : 'timeout'}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} financial analysis attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Financial analysis failed after all retries');
  }

  private async executeFinancialAnalysisInternal(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting financial analysis with data:', {
        documentsCount: data.documents?.length,
        additionalContext: data.additionalContext,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        documentTypes: data.documents?.map(d => ({ id: d.id, name: d.name, type: d.type })),
        apiBaseUrl: API_BASE_URL,
        hasToken: !!localStorage.getItem('riskninja-token')
      });

      // Get current token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/financial-analysis/execute`;
      console.log('📡 Making request to:', requestUrl);

      // No timeout - let the server's 20-minute timeout handle it
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Financial analysis request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`Failed to execute financial analysis: ${response.status} ${response.statusText}`);
      }

      const initialResult = await response.json();
      console.log('✅ Financial analysis started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      // Check if this is the new async response format
      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');
        const finalResult = await this.pollFinancialAnalysisJobStatus(initialResult.jobId, token);
        return finalResult;
      }

      // Old format (synchronous) - return directly
      return initialResult;

    } catch (error) {
      console.error('❌ Error executing financial analysis:', error);
      console.error('Error details:', {
        documentsArray: data.documents?.map(d => ({ id: d.id, name: d.name })),
        dataProvided: {
          documentsCount: data.documents?.length,
          hasClientId: !!data.clientId,
          hasProjectId: !!data.projectId,
          hasAdditionalContext: !!data.additionalContext
        }
      });

      throw error;
    }
  }

  private async pollFinancialAnalysisJobStatus(jobId: string, token: string, maxAttempts: number = 600): Promise<any> {
    const pollInterval = 3000; // 3 seconds - poll for up to 30 minutes
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Financial Analysis Progress: ${progress}% - ${this.getFinancialAnalysisProgressMessage(progress)}`);

      localStorage.setItem('financialAnalysisProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('financialAnalysisProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/financial-analysis/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Financial Analysis Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Financial analysis completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          localStorage.removeItem('financialAnalysisProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('financialAnalysisProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        // Re-throw job failures immediately - don't swallow them
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('financialAnalysisProgress');
          throw new Error('Job status polling timeout');
        }
      }
    }

    localStorage.removeItem('financialAnalysisProgress');
    throw new Error('Job completion timeout (exceeded 15 minutes)');
  }

  private getFinancialAnalysisProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading financial documents...';
    if (progress < 30) return 'Preparing documents...';
    if (progress < 40) return 'Generating analysis prompts...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI analyzing financial statements... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations and risk assessment...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  // Client Renewal Presentation - with retry logic
  async executeClientRenewalPresentation(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing client renewal presentation (attempt ${attempt}/${maxRetries})...`);
        return await this.executeClientRenewalPresentationInternal(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Client renewal presentation attempt ${attempt} failed:`, error);

        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');
        const isTimeout = error instanceof Error && (
          error.message.includes('timeout') || error.message.includes('aborted')
        );

        if ((isJobFailure || isTimeout) && attempt < maxRetries) {
          const retryDelay = attempt * 5;
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${isJobFailure ? 'backend job failure' : 'timeout'}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} client renewal presentation attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Client renewal presentation failed after all retries');
  }

  private async executeClientRenewalPresentationInternal(data: {
    documents: any[];
    additionalContext: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting client renewal presentation with data:', {
        documentsCount: data.documents?.length,
        additionalContext: data.additionalContext,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        apiBaseUrl: API_BASE_URL,
        hasToken: !!localStorage.getItem('riskninja-token')
      });

      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/client-renewal-presentation/execute`;
      console.log('📡 Making request to:', requestUrl);

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Client renewal presentation request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`Failed to execute client renewal presentation: ${response.status} ${response.statusText}`);
      }

      const initialResult = await response.json();
      console.log('✅ Client renewal presentation started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');
        const finalResult = await this.pollClientRenewalPresentationJobStatus(initialResult.jobId, token);
        return finalResult;
      }

      return initialResult;

    } catch (error) {
      console.error('❌ Error executing client renewal presentation:', error);
      throw error;
    }
  }

  private async pollClientRenewalPresentationJobStatus(jobId: string, token: string, maxAttempts: number = 600): Promise<any> {
    const pollInterval = 3000;
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Client Renewal Presentation Progress: ${progress}% - ${this.getClientRenewalPresentationProgressMessage(progress)}`);

      localStorage.setItem('clientRenewalPresentationProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('clientRenewalPresentationProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/client-renewal-presentation/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Client Renewal Presentation Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Client renewal presentation completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          localStorage.removeItem('clientRenewalPresentationProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('clientRenewalPresentationProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('clientRenewalPresentationProgress');
          throw new Error('Job status polling timeout');
        }
      }
    }

    localStorage.removeItem('clientRenewalPresentationProgress');
    throw new Error('Job completion timeout (exceeded 15 minutes)');
  }

  private getClientRenewalPresentationProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading insurance documents...';
    if (progress < 30) return 'Preparing documents...';
    if (progress < 40) return 'Generating analysis prompts...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI analyzing renewal documents... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations and coverage analysis...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  // Document management
  async getDocumentsByTags(clientId: string, projectId?: string, tags: string[] = []): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/documents/by-tags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify({
          clientId,
          projectId,
          tags
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get documents by tags');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting documents by tags:', error);
      throw error;
    }
  }

  async tagDocuments(documents: any[], tags: string[]): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/documents/tag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify({
          documents,
          tags
        })
      });

      if (!response.ok) {
        throw new Error('Failed to tag documents');
      }

      return await response.json();
    } catch (error) {
      console.error('Error tagging documents:', error);
      throw error;
    }
  }

  // Export functionality
  async exportReport(report: any, filename: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/reports/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        },
        body: JSON.stringify({
          report,
          format: 'pdf'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to export report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }

  // ===== ENHANCED WORKFLOW METHODS =====

  // Execute universal workflow with structured JSON output
  async executeUniversalWorkflow(request: WorkflowExecutionRequest): Promise<any> {
    try {
      return await universalAnalysisService.executeWorkflow(request);
    } catch (error) {
      console.error('Universal workflow execution failed:', error);
      throw error;
    }
  }

  // Execute enhanced comparison with structured output
  async executeEnhancedComparison(
    documentIds: string[], 
    additionalFacts?: string, 
    options?: any
  ): Promise<any> {
    try {
      return await universalAnalysisService.executeEnhancedComparison({
        documentIds,
        additionalFacts,
        options
      });
    } catch (error) {
      console.error('Enhanced comparison failed:', error);
      throw error;
    }
  }

  // Execute workflow-based analysis
  async executeWorkflowAnalysis(
    documents: any[],
    workflowType: string,
    additionalContext?: string,
    customerId?: string,
    projectId?: string
  ): Promise<any> {
    try {
      const documentIds = documents.map(doc => doc.id);
      
      // Validate workflow requirements
      const validation = universalAnalysisService.validateDocumentRequirements(
        workflowType, 
        documentIds.length
      );
      
      if (!validation.valid) {
        throw new Error(validation.message);
      }

      const request: WorkflowExecutionRequest = {
        workflowType: workflowType as any,
        documents: documentIds.map(id => ({ id })),
        additionalContext,
        customerId,
        projectId,
        options: {
          smartCitations: true,
          enhanceCitations: true,
          validateCitations: true
        }
      };

      return await this.executeUniversalWorkflow(request);
    } catch (error) {
      console.error('Workflow analysis failed:', error);
      throw error;
    }
  }

  // Get workflow execution history
  async getWorkflowHistory(workflowType?: string, limit: number = 20): Promise<any> {
    try {
      return await universalAnalysisService.listWorkflowExecutions(workflowType, limit);
    } catch (error) {
      console.error('Failed to get workflow history:', error);
      throw error;
    }
  }

  // Handle citation navigation
  handleCitationClick(citation: any, navigate: (path: string, options?: any) => void): void {
    universalAnalysisService.handleCitationClick(citation, navigate);
  }

  // Get available workflow types with their requirements
  getAvailableWorkflows(): Array<{
    type: string;
    title: string;
    description: string;
    minDocuments: number;
    icon: string;
  }> {
    return [
      {
        type: 'policy_comparison',
        title: 'Policy Comparison',
        description: 'Compare multiple insurance policies side-by-side',
        minDocuments: 2,
        icon: '⚖️'
      },
      {
        type: 'policy_analysis',
        title: 'Policy Analysis',
        description: 'Comprehensive analysis of individual policy',
        minDocuments: 1,
        icon: '🔍'
      },
      {
        type: 'loss_run_analysis',
        title: 'Loss Run Analysis',
        description: 'Analyze claims history and loss trends',
        minDocuments: 1,
        icon: '📈'
      },
      {
        type: 'research_analysis',
        title: 'Research Analysis',
        description: 'Market research and competitive analysis',
        minDocuments: 1,
        icon: '🔬'
      },
      {
        type: 'risk_assessment',
        title: 'Risk Assessment',
        description: 'Comprehensive risk evaluation and mitigation',
        minDocuments: 1,
        icon: '⚠️'
      }
    ];
  }

  // Validate if documents can be used for a specific workflow
  validateWorkflowDocuments(workflowType: string, documents: any[]): { valid: boolean; message?: string } {
    return universalAnalysisService.validateDocumentRequirements(workflowType, documents.length);
  }

  // Export enhanced analysis results
  async exportEnhancedAnalysis(workflowId: string, format: 'pdf' | 'word' | 'excel' | 'json' = 'pdf'): Promise<Blob> {
    try {
      return await universalAnalysisService.exportAnalysis(workflowId, format);
    } catch (error) {
      console.error('Enhanced export failed:', error);
      throw error;
    }
  }

  // Delete document
  async deleteDocument(documentId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete document (${response.status}): ${errorText}`);
      }

      console.log('✅ Document deleted successfully:', documentId);
    } catch (error) {
      console.error('❌ Error deleting document:', error);
      throw error;
    }
  }

  // Regenerate report with additional context and chat history. Now runs
  // through the same async job queue as a fresh /unified/execute workflow:
  // the POST returns a jobId, and we poll /unified/status/:jobId until the
  // job completes. The returned shape ({ success, report }) is preserved
  // so callers don't need to change.
  async regenerateReport(
    data: {
      originalWorkflowData: any;
      selectedDocuments?: {
        originalDocIds: string[];
        additionalDocIds: string[];
      };
      additionalDocuments?: any[];
      includeChatHistory?: boolean;
      chatHistory?: any[];
      options?: any;
    },
    onProgress?: (progress: number, status: string) => void
  ): Promise<any> {
    try {
      console.log('🔄 Starting selective report regeneration (queued)...', {
        selectedOriginalDocs: data.selectedDocuments?.originalDocIds?.length || 0,
        selectedAdditionalDocs: data.selectedDocuments?.additionalDocIds?.length || 0,
        totalAdditionalDocs: data.additionalDocuments?.length || 0,
        includeChatHistory: data.includeChatHistory || false,
        chatMessages: data.chatHistory?.length || 0,
        workflowType: data.options?.workflowType || data.originalWorkflowData?.workflowType
      });

      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/api/workflows/regenerate-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to regenerate report' }));
        throw new Error(errorData.error || `Regeneration failed with status ${response.status}`);
      }

      const enqueueResult = await response.json();

      // Async job: poll until completion. The unified status endpoint is
      // job-type-agnostic (it just looks up by id in asyncJobService).
      if (enqueueResult.status === 'processing' && enqueueResult.jobId) {
        console.log(`🔄 Regeneration enqueued, polling job ${enqueueResult.jobId}...`);
        const result = await this.pollUnifiedJobStatus(enqueueResult.jobId, token, onProgress);
        console.log('✅ Selective report regeneration completed:', {
          success: result?.success,
          reportId: result?.report?.id,
          documentsProcessed: result?.report?.documents?.length,
          workflowType: result?.report?.workflowType
        });
        return result;
      }

      // Defensive: shouldn't happen now, but keep a passthrough in case the
      // server falls back to a synchronous response.
      return enqueueResult;
    } catch (error) {
      console.error('Error regenerating report:', error);
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // UNIFIED WORKFLOW METHODS (NEW SYSTEM)
  // These methods use the unified workflow endpoints for all workflow types
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Execute any workflow type using the unified endpoint
   * This is the single entry point for all workflow execution
   */
  async executeUnifiedWorkflow(
    workflowType: 'loss_run_analysis' | 'renewal_analysis' | 'year_over_year' | 'coverage_analysis' | 'insurance_requirements' | 'document_comparison' | 'policy_comparison' | 'research' | 'cyber_auto' | 'cyber_benchmark' | 'cyber_renewal_delta' | 'cyber_war_systemic' | 'cyber_ransomware_trap' | 'cyber_quote_comparison' | 'do_auto' | 'do_benchmark' | 'do_renewal_delta' | 'do_distress_scanner' | 'do_emerging_exposure' | 'home_auto' | 'home_owner_benchmark' | 'home_tenant_condo_benchmark' | 'home_quote_comparison' | 'home_renewal_delta' | 'home_dp_rental_benchmark' | 'home_dp_rental_renewal_delta' | 'home_hnw_benchmark' | 'plines_auto' | 'pa_benchmark' | 'pa_quote_comparison' | 'pa_renewal_delta' | 'umb_benchmark' | 'umb_quote_comparison' | 'umb_renewal_delta' | 'rt_auto' | 'rt_requirements_reviewer' | 'rt_conformance_alignment' | 'rt_compliance_verifier' | 'premdev_auto' | 'premdev_workers_comp' | 'premdev_prof_liability' | 'premdev_general_liability' | 'premdev_property' | 'premdev_package_bop' | 'lossrun_auto' | 'lossrun_fact_engine' | 'lossrun_mitigation_wc' | 're_auto' | 're_policy_extraction' | 're_risk_transfer' | 're_br_financials' | 're_zone_extractor' | 're_portfolio_brief' | 'cl_auto' | 'cl_gl_benchmark' | 'cl_property_benchmark' | 'cl_commercial_auto_benchmark' | 'cl_inland_marine_benchmark' | 'cl_excess_benchmark' | 'cl_package_benchmark' | 'cl_bop_benchmark' | 'cl_nytpao_flow' | 'cl_package_quote_comparison' | 'cl_bop_quote_comparison' | 'cl_xarch_quote_comparison' | 'cl_package_renewal_delta' | 'cl_bop_renewal_delta' | 'ca_auto' | 'ca_coverage_determination' | 'ca_portfolio_determination' | 'ca_carrier_position_audit' | 'ntr_auto' | 'ntr_first_party' | 'ntr_property_liability' | 'ntr_mgmt_professional',
    request: {
      documents: any[];
      additionalDocuments?: any[];  // Supplemental context documents
      clientId: string;
      projectId?: string;
      context?: string;
      pastReports?: any[];
      additionalInfo?: string;
    },
    onProgress?: (progress: number, status: string) => void
  ): Promise<any> {
    const token = localStorage.getItem('riskninja-token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log(`🚀 [UNIFIED] Starting ${workflowType} workflow...`);
    console.log(`📄 [UNIFIED] Documents: ${request.documents?.length || 0}, Additional: ${request.additionalDocuments?.length || 0}, Context: ${request.context?.substring(0, 50) || 'none'}...`);

    try {
      // Execute the workflow via unified endpoint
      const executeUrl = `${API_BASE_URL}/api/workflows/unified/execute/${workflowType}`;

      const response = await fetchWithActivity(executeUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to start ${workflowType} workflow`);
      }

      const result = await response.json();

      // If processing is async, poll for completion
      if (result.status === 'processing' && result.jobId) {
        console.log(`🔄 [UNIFIED] Async processing started, polling job ${result.jobId}...`);
        return await this.pollUnifiedJobStatus(result.jobId, token, onProgress);
      }

      // Immediate result (unlikely)
      return result;

    } catch (error) {
      console.error(`❌ [UNIFIED] Workflow execution failed:`, error);
      throw error;
    }
  }

  /**
   * Poll for unified workflow job completion
   * Single polling implementation for all workflow types
   */
  private async pollUnifiedJobStatus(
    jobId: string,
    token: string,
    onProgress?: (progress: number, status: string) => void,
    maxAttempts: number = 450 // 15 minutes at 2s intervals
  ): Promise<any> {
    const pollInterval = 2000;
    let attempts = 0;

    while (attempts < maxAttempts) {
      attempts++;

      try {
        // Wait before polling (except first attempt)
        if (attempts > 1) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }

        const statusUrl = `${API_BASE_URL}/api/workflows/unified/status/${jobId}`;

        const response = await fetchWithActivity(statusUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Job not found. It may have expired.');
          }
          continue; // Retry on other errors
        }

        const statusData = await response.json();

        // Progress callback
        if (onProgress && statusData.job?.progress !== undefined) {
          onProgress(statusData.job.progress, statusData.job.status);
        }

        // Check completion
        if (statusData.job?.status === 'completed') {
          console.log('✅ [UNIFIED] Workflow job completed successfully');
          return statusData.job.result;
        }

        // Check failure
        if (statusData.job?.status === 'failed') {
          throw new Error(statusData.job.error || 'Workflow execution failed');
        }

        // Log progress periodically
        if (attempts % 15 === 0) {
          console.log(`🔄 [UNIFIED] Polling ${attempts}/${maxAttempts} - ${statusData.job?.progress || 0}%`);
        }

      } catch (error: any) {
        console.error(`❌ [UNIFIED] Polling error (attempt ${attempts}):`, error);

        if (error.message?.includes('not found') || error.message?.includes('expired')) {
          throw error;
        }

        if (attempts >= maxAttempts) {
          throw new Error(`Job polling timeout after ${maxAttempts} attempts`);
        }
      }
    }

    throw new Error(`Job polling timeout after ${maxAttempts} attempts`);
  }

  /**
   * Get available workflow types from the server
   */
  async getUnifiedWorkflowTypes(): Promise<Array<{
    type: string;
    displayName: string;
    minDocuments: number;
    maxDocuments?: number;
    requiresProjectId: boolean;
    supportsPastReports: boolean;
  }>> {
    const token = localStorage.getItem('riskninja-token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetchWithActivity(`${API_BASE_URL}/api/workflows/unified/types`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch workflow types');
    }

    const data = await response.json();
    return data.workflows;
  }

  /**
   * Execute Rate Analysis workflow
   */
  async executeRateAnalysis(data: {
    documents: any[];
    additionalDocuments?: any[];
    analysisTier: string;
    additionalInfo?: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    // Retry wrapper for backend job failures
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing rate analysis (attempt ${attempt}/${maxRetries})...`);
        return await this.executeRateAnalysisInternal(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Rate analysis attempt ${attempt} failed:`, error);

        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');
        const isTimeout = error instanceof Error && (
          error.message.includes('timeout') || error.message.includes('aborted')
        );

        if ((isJobFailure || isTimeout) && attempt < maxRetries) {
          const retryDelay = attempt * 5;
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${isJobFailure ? 'backend job failure' : 'timeout'}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} rate analysis attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Rate analysis failed after all retries');
  }

  private async executeRateAnalysisInternal(data: {
    documents: any[];
    additionalDocuments?: any[];
    analysisTier: string;
    additionalInfo?: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting rate analysis with data:', {
        documentsCount: data.documents?.length,
        additionalDocumentsCount: data.additionalDocuments?.length || 0,
        analysisTier: data.analysisTier,
        additionalInfo: data.additionalInfo,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        documentTypes: data.documents?.map(d => ({ id: d.id, name: d.name, type: d.type })),
        apiBaseUrl: API_BASE_URL,
        hasToken: !!localStorage.getItem('riskninja-token')
      });

      // Get current token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/rate_analysis`;
      console.log('📡 Making request to:', requestUrl);

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Rate analysis failed with status:', response.status, errorText);

        if (response.status === 403) {
          console.error('❌ 403 Forbidden - Check if token is valid and project access is correct');
        }

        throw new Error(`Failed to analyze rates (${response.status}): ${errorText}`);
      }

      const initialResult = await response.json();
      console.log('✅ Rate analysis started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      // Check if this is the async response format
      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');
        const token = localStorage.getItem('riskninja-token') || '';
        const finalResult = await this.pollRateAnalysisJobStatus(initialResult.jobId, token);
        return finalResult;
      }

      // Old format (synchronous) - return directly
      return initialResult;
    } catch (error) {
      console.error('❌ Error analyzing rates:', error);

      console.error('❌ Error details:', {
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        dataProvided: {
          documentsCount: data.documents?.length,
          hasAnalysisTier: !!data.analysisTier,
          hasClientId: !!data.clientId,
          hasProjectId: !!data.projectId
        }
      });

      throw error;
    }
  }

  private async pollRateAnalysisJobStatus(jobId: string, token: string, maxAttempts: number = 600): Promise<any> {
    const pollInterval = 3000; // 3 seconds - poll for up to 30 minutes
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Rate Analysis Progress: ${progress}% - ${this.getRateAnalysisProgressMessage(progress)}`);

      localStorage.setItem('rateAnalysisProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('rateAnalysisProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/rate_analysis/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Rate Analysis Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Rate analysis completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          localStorage.removeItem('rateAnalysisProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('rateAnalysisProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('rateAnalysisProgress');
          throw new Error('Job status polling timeout');
        }
      }
    }

    localStorage.removeItem('rateAnalysisProgress');
    throw new Error('Job completion timeout (exceeded 30 minutes)');
  }

  private getRateAnalysisProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading rating documents...';
    if (progress < 30) return 'Extracting premium data...';
    if (progress < 40) return 'Preparing rate analysis prompts...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI analyzing rates and classifications... (this may take a few minutes)';
    if (progress < 80) return 'Processing driver hierarchy...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  // ===== PORTFOLIO ANALYZER =====
  async executePortfolioAnalyzer(data: {
    documents: any[];
    additionalDocuments?: any[];
    scenario?: string;
    additionalInfo?: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    // Retry wrapper for backend job failures
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing portfolio analyzer (attempt ${attempt}/${maxRetries})...`);
        return await this.executePortfolioAnalyzerInternal(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Portfolio analyzer attempt ${attempt} failed:`, error);

        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');
        const isTimeout = error instanceof Error && (
          error.message.includes('timeout') || error.message.includes('aborted')
        );

        if ((isJobFailure || isTimeout) && attempt < maxRetries) {
          const retryDelay = attempt * 5;
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${isJobFailure ? 'backend job failure' : 'timeout'}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} portfolio analyzer attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Portfolio analyzer failed after all retries');
  }

  private async executePortfolioAnalyzerInternal(data: {
    documents: any[];
    additionalDocuments?: any[];
    scenario?: string;
    additionalInfo?: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting portfolio analyzer with data:', {
        documentsCount: data.documents?.length,
        additionalDocumentsCount: data.additionalDocuments?.length || 0,
        scenario: data.scenario?.substring(0, 100),
        additionalInfo: data.additionalInfo,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
        documentTypes: data.documents?.map(d => ({ id: d.id, name: d.name, type: d.type })),
        apiBaseUrl: API_BASE_URL,
        hasToken: !!localStorage.getItem('riskninja-token')
      });

      // Get current token
      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/unified/execute/portfolio_analyzer`;
      console.log('📡 Making request to:', requestUrl);

      // Map frontend fields to unified workflow format
      const unifiedPayload = {
        documents: data.documents,
        additionalDocuments: data.additionalDocuments || [],
        clientId: data.clientId,
        projectId: data.projectId,
        context: data.scenario,
        additionalInfo: data.additionalInfo,
        reportTitle: data.reportTitle
      };

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(unifiedPayload)
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Portfolio analyzer failed with status:', response.status, errorText);

        if (response.status === 403) {
          console.error('❌ 403 Forbidden - Check if token is valid and project access is correct');
        }

        throw new Error(`Failed to analyze portfolio (${response.status}): ${errorText}`);
      }

      const initialResult = await response.json();
      console.log('✅ Portfolio analyzer started, initial response:', {
        success: !!initialResult.success,
        status: initialResult.status,
        jobId: initialResult.jobId,
        message: initialResult.message
      });

      // Check if this is the async response format
      if (initialResult.status === 'processing' && initialResult.jobId) {
        console.log('🔄 Async processing detected, polling for completion...');
        const token = localStorage.getItem('riskninja-token') || '';
        const finalResult = await this.pollPortfolioAnalyzerJobStatus(initialResult.jobId, token);
        return finalResult;
      }

      // Old format (synchronous) - return directly
      return initialResult;
    } catch (error) {
      console.error('❌ Error analyzing portfolio:', error);

      console.error('❌ Error details:', {
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        dataProvided: {
          documentsCount: data.documents?.length,
          hasScenario: !!data.scenario,
          hasClientId: !!data.clientId,
          hasProjectId: !!data.projectId
        }
      });

      throw error;
    }
  }

  private async pollPortfolioAnalyzerJobStatus(jobId: string, token: string, maxAttempts: number = 600): Promise<any> {
    const pollInterval = 3000; // 3 seconds - poll for up to 30 minutes
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      console.log(`⏳ Portfolio Analyzer Progress: ${progress}% - ${this.getPortfolioAnalyzerProgressMessage(progress)}`);

      localStorage.setItem('portfolioAnalyzerProgress', JSON.stringify({
        jobId,
        progress,
        status,
        timestamp: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('portfolioAnalyzerProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/unified/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        console.log(`📊 Portfolio Analyzer Job ${jobId} status (attempt ${attempts}/${maxAttempts}):`, {
          status,
          progress
        });

        emitProgress(progress, status);

        if (status === 'completed') {
          console.log('✅ Portfolio analyzer completed!', statusResult.job.result);
          emitProgress(100, 'completed');
          localStorage.removeItem('portfolioAnalyzerProgress');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          localStorage.removeItem('portfolioAnalyzerProgress');
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`❌ Error polling job status (attempt ${attempts}):`, error);
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          localStorage.removeItem('portfolioAnalyzerProgress');
          throw new Error('Job status polling timeout');
        }
      }
    }

    localStorage.removeItem('portfolioAnalyzerProgress');
    throw new Error('Job completion timeout (exceeded 30 minutes)');
  }

  private getPortfolioAnalyzerProgressMessage(progress: number): string {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading policy documents...';
    if (progress < 30) return 'Mapping portfolio structure...';
    if (progress < 40) return 'Decomposing scenario into components...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 60) return 'Performing vertical layer analysis...';
    if (progress < 70) return 'Performing horizontal coverage analysis... (this may take a few minutes)';
    if (progress < 80) return 'Analyzing gaps and overlaps...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  }

  // ===== HOMEOWNERS INSURANCE COMPARISON =====
  async executeHomeownersComparison(data: {
    documents: any[];
    additionalInfo?: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Executing homeowners comparison (attempt ${attempt}/${maxRetries})...`);
        return await this.executeHomeownersComparisonInternal(data);
      } catch (error) {
        lastError = error as Error;
        console.warn(`❌ Homeowners comparison attempt ${attempt} failed:`, error);

        const isJobFailure = error instanceof Error && error.message.startsWith('Job failed:');
        const isTimeout = error instanceof Error && (
          error.message.includes('timeout') || error.message.includes('aborted')
        );

        if ((isJobFailure || isTimeout) && attempt < maxRetries) {
          const retryDelay = attempt * 5;
          console.log(`🔄 Retrying in ${retryDelay} seconds due to ${isJobFailure ? 'backend job failure' : 'timeout'}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
        } else if (attempt === maxRetries) {
          console.error(`❌ All ${maxRetries} homeowners comparison attempts failed. Final error:`, lastError);
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Homeowners comparison failed after all retries');
  }

  private async executeHomeownersComparisonInternal(data: {
    documents: any[];
    additionalInfo?: string;
    clientId: string;
    projectId?: string;
    reportTitle?: string;
  }): Promise<any> {
    try {
      console.log('🔄 Starting homeowners comparison with data:', {
        documentsCount: data.documents?.length,
        additionalInfo: data.additionalInfo,
        clientId: data.clientId,
        projectId: data.projectId,
        documentIds: data.documents?.map(d => d.id),
      });

      const token = localStorage.getItem('riskninja-token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('Authentication token is missing');
      }

      const requestUrl = `${API_BASE_URL}/api/workflows/unified/execute/homeowners_comparison`;

      const requestBody = {
        documents: data.documents.map(d => d.id || d),
        clientId: data.clientId,
        projectId: data.projectId,
        context: data.additionalInfo,
        reportTitle: data.reportTitle,
      };

      console.log('📤 Sending homeowners comparison request:', { url: requestUrl });

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }

      const result = await response.json();

      if (result.jobId) {
        console.log(`🔄 Homeowners comparison job queued: ${result.jobId}`);
        return await this.pollHomeownersComparisonJobStatus(result.jobId, token);
      }

      return result;
    } catch (error) {
      console.error('❌ Error in homeowners comparison:', error);
      throw error;
    }
  }

  private async pollHomeownersComparisonJobStatus(jobId: string, token: string, maxAttempts: number = 600): Promise<any> {
    const pollInterval = 3000;
    let attempts = 0;

    const emitProgress = (progress: number, status: string) => {
      window.dispatchEvent(new CustomEvent('homeownersComparisonProgress', {
        detail: { jobId, progress, status }
      }));
    };

    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusUrl = `${API_BASE_URL}/api/workflows/unified/status/${jobId}`;
        const response = await fetch(statusUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status (${response.status})`);
        }

        const statusResult = await response.json();
        const progress = statusResult.job?.progress || 0;
        const status = statusResult.job?.status || 'processing';

        emitProgress(progress, status);

        if (status === 'completed') {
          emitProgress(100, 'completed');
          return statusResult.job.result;
        }

        if (status === 'failed') {
          throw new Error(`Job failed: ${statusResult.job.error || 'Unknown error'}`);
        }
      } catch (error) {
        if (error instanceof Error && error.message.startsWith('Job failed:')) {
          throw error;
        }
        if (attempts >= maxAttempts) {
          throw new Error('Job status polling timeout');
        }
      }
    }

    throw new Error('Job completion timeout (exceeded 30 minutes)');
  }
}

export const workflowService = new WorkflowService();